import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTranslation, Trans } from 'react-i18next';
import TutorSchoolAffiliation from '../tutor/TutorSchoolAffiliation';
import { OrganizationType } from '../../models/Organization';
import { ChatPartnerOption } from '../../utils/chatPartners';
import { chatAvatarColor } from '../../utils/chat';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';

interface ChatPartnerPickerProps {
  partners: ChatPartnerOption[];
  value: ChatPartnerOption | null;
  onChange: (partner: ChatPartnerOption | null) => void;
  disabled?: boolean;
}

function normalizeSearchQuery(query: string): string {
  return query.trim().toLocaleLowerCase();
}

function partnerMatchesQuery(partner: ChatPartnerOption, query: string): boolean {
  if (!query) return true;
  const haystacks = [
    partner.name,
    partner.tutorSpaceName,
    partner.hostOrganizationName,
    partner.userBandName,
  ];
  return haystacks.some((value) => (value ?? '').toLocaleLowerCase().includes(query));
}

function partnerHasTutorRole(partner: ChatPartnerOption): boolean {
  return (
    partner.roles?.includes('TUTOR') === true || Boolean(partner.tutorSpaceName?.trim())
  );
}

/** Plain musician/user — not tutor/host staff; uses userBandName for “Plays in …”. */
function isPlainUserPartner(partner: ChatPartnerOption): boolean {
  const roles = partner.roles ?? [];
  if (roles.length === 0) {
    return Boolean(partner.userBandName?.trim());
  }
  return (
    roles.includes('USER') &&
    !roles.includes('TUTOR') &&
    !roles.includes('HOST') &&
    !roles.includes('ADMIN')
  );
}

const ChatPartnerPicker: React.FC<ChatPartnerPickerProps> = ({
  partners,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSearch('');
  }, [partners]);

  const normalizedSearch = normalizeSearchQuery(search);

  const filteredPartners = useMemo(
    () => partners.filter((partner) => partnerMatchesQuery(partner, normalizedSearch)),
    [partners, normalizedSearch]
  );

  const handleSelect = (partner: ChatPartnerOption) => {
    if (disabled) return;
    onChange(value?.userId === partner.userId ? null : partner);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        disabled={disabled}
        placeholder={t('chat.create.partnerPlaceholder', 'Search by name')}
        aria-label={t('chat.create.partnerPlaceholder', 'Search by name')}
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ maxHeight: 'none' }}>
              <Chip
                size="small"
                label={
                  normalizedSearch
                    ? t('chat.create.filteredRecipientCount', '{{count}} of {{total}}', {
                        count: filteredPartners.length,
                        total: partners.length,
                      })
                    : t('chat.create.recipientCount', '{{count}} available', {
                        count: filteredPartners.length,
                      })
                }
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: (theme) => alpha(theme.palette.common.white, 0.08),
                  color: 'text.secondary',
                  border: 1,
                  borderColor: 'divider',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.04),
          },
        }}
      />

      <Box
        role="listbox"
        aria-label={t('chat.create.selectRecipient', 'Select recipient')}
        sx={{
          maxHeight: 360,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pr: 0.25,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 999,
          },
        }}
      >
        {filteredPartners.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'rgba(255,255,255,0.02)',
            }}
          >
            <PersonOutlineRoundedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.75 }} />
            <Typography variant="body2" color="text.secondary">
              {t('chat.create.noSearchResults', 'No one matches that name.')}
            </Typography>
          </Box>
        ) : (
          filteredPartners.map((partner) => {
            const selected = value?.userId === partner.userId;
            const showTutorAffiliation = partnerHasTutorRole(partner);
            const hostOrganizationName =
              partner.organizationType === OrganizationType.INDIVIDUAL_TUTOR
                ? null
                : partner.hostOrganizationName?.trim() || null;
            const userBandName =
              isPlainUserPartner(partner) ? partner.userBandName?.trim() || null : null;

            return (
              <Box
                key={partner.userId}
                role="option"
                aria-selected={selected}
                tabIndex={disabled ? -1 : 0}
                onClick={() => handleSelect(partner)}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelect(partner);
                  }
                }}
                sx={(theme) => ({
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  minHeight: 72,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  cursor: disabled ? 'default' : 'pointer',
                  border: 1,
                  borderColor: selected ? alpha(theme.palette.primary.main, 0.55) : 'divider',
                  bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : 'rgba(255,255,255,0.02)',
                  transition: theme.transitions.create(['border-color', 'background-color', 'transform'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  opacity: disabled ? 0.6 : 1,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    borderRadius: '0 2px 2px 0',
                    bgcolor: selected ? theme.palette.primary.main : 'transparent',
                    transition: theme.transitions.create('background-color', {
                      duration: theme.transitions.duration.shorter,
                    }),
                  },
                  ...(!disabled && {
                    '&:hover': {
                      borderColor: selected
                        ? alpha(theme.palette.primary.main, 0.65)
                        : alpha(theme.palette.common.white, 0.22),
                      bgcolor: selected
                        ? alpha(theme.palette.primary.main, 0.16)
                        : 'rgba(255,255,255,0.05)',
                      transform: 'translateX(2px)',
                    },
                    '&:focus-visible': {
                      outline: 2,
                      outlineColor: theme.palette.primary.main,
                      outlineOffset: 2,
                    },
                  }),
                })}
              >
                <Avatar
                  src={partner.profileImageUrl ?? undefined}
                  sx={{
                    width: 48,
                    height: 48,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    bgcolor: chatAvatarColor(partner.userId),
                    flexShrink: 0,
                  }}
                >
                  {tutorAvatarInitials(partner.name ?? '')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight={selected ? 700 : 500}
                    color={partner.name ? 'text.primary' : 'text.secondary'}
                    noWrap
                  >
                    {partner.name?.trim() || t('common.unnamedUser', 'Unnamed user')}
                  </Typography>
                  {showTutorAffiliation ? (
                    <Box
                      sx={{
                        '& > .MuiStack-root': { mt: 0.25, mb: 0 },
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <TutorSchoolAffiliation
                        organizationType={partner.organizationType}
                        spaceName={partner.tutorSpaceName}
                        variant="body2"
                      />
                    </Box>
                  ) : null}
                  {hostOrganizationName ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap sx={{ mt: 0.25 }}>
                      <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" component="span" noWrap>
                        <Trans
                          i18nKey="chat.create.hostAffiliation"
                          defaults="Admin at <orgName>{{name}}</orgName>"
                          values={{ name: hostOrganizationName }}
                          components={{
                            orgName: (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Stack>
                  ) : null}
                  {userBandName ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap sx={{ mt: 0.25 }}>
                      <MusicNoteOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" component="span" noWrap>
                        <Trans
                          i18nKey="chat.create.userBandAffiliation"
                          defaults="Plays in <bandName>{{name}}</bandName>"
                          values={{ name: userBandName }}
                          components={{
                            bandName: (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Stack>
                  ) : null}
                </Box>
                {selected ? (
                  <CheckCircleRoundedIcon
                    sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }}
                    aria-hidden
                  />
                ) : null}
              </Box>
            );
          })
        )}
      </Box>
    </Stack>
  );
};

export default ChatPartnerPicker;
